import React, { ReactEventHandler } from "react";
import has from "lodash/has";
import omit from "lodash/omit";
import classNames from "classnames";

import Image, { ImageLoader, StaticImageData } from "next/legacy/image";

import { isCloudinaryUrl } from "@stadium/helpers/cloudinary";
import { getFileExtension, isSvgFormat } from "@stadium/helpers/logo";
import { getOptimizedImageUrl, isUnoptimizedImageUrl } from "@stadium/helpers/multimedia";

import styles from "./main.module.scss";

export interface MultimediaProps {
  type: "image" | "video" | "iframe";
  src: string;
  loader?: ImageLoader;
  alt?: string;
  className?: string;
  testId?: string;
  containerClass?: string;
  embedCode?: string;
  height?: number;
  width?: number;
  priority?: boolean;
  isNextImage?: boolean;
  noBlur?: boolean;
  lazyBoundary?: string;
  onError?: ReactEventHandler<HTMLImageElement>;
  cloudinaryContainerClass?: string;
  unoptimized?: boolean;
  loading?: React.ImgHTMLAttributes<HTMLImageElement>["loading"];
  legacyLazyload?: boolean;
  rewriteToOptimizedCdnUrl?: boolean;
  fetchPriority?: "high" | "low" | "auto";
  decoding?: "sync" | "async" | "auto";
}

export type ImageProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  type: "image";
} & Omit<MultimediaProps, "type">;

const BLUR_DATA_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAZAAAAGQAQMAAAC6caSPAAAAAXNSR0IB2cksfwAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAAANQTFRF7vD13W0nXgAAACpJREFUeJztwTEBAAAAwqD1T+1vBqAAAAAAAAAAAAAAAAAAAAAAAAAAgDdPsAABORaXeAAAAABJRU5ErkJggg==";

const IMAGE_URL = process.env.NEXT_PUBLIC_IMAGE_SERVER
  ? process.env.NEXT_PUBLIC_IMAGE_SERVER
  : undefined;

const myImageLoader: ImageLoader = IMAGE_URL
  ? ({ src, width, quality }) => {
      if (isUnoptimizedImageUrl(src)) {
        return getOptimizedImageUrl(src, width, undefined, quality);
      }
      const imageLoaderUrl = new URL(IMAGE_URL);

      if (width) {
        imageLoaderUrl.searchParams.append("w", String(width));
      }
      imageLoaderUrl.searchParams.append("url", src);
      imageLoaderUrl.searchParams.append("q", String(quality || 75));
      return imageLoaderUrl.toString();
    }
  : undefined;

const Multimedia = (props: MultimediaProps | ImageProps) => {
  const {
    type,
    src,
    loader = myImageLoader,
    alt,
    className = "",
    testId,
    priority = false,
    isNextImage = true,
    noBlur = false,
    onError,
    cloudinaryContainerClass = "",
    unoptimized,
    legacyLazyload = true,
    rewriteToOptimizedCdnUrl = true,
    fetchPriority,
    decoding,
    loading,
  } = props;

  let restProps = {};

  const isCloudinaryImage = typeof src === "string" ? isCloudinaryUrl(src) : false;

  switch (type) {
    case "image": {
      restProps = omit(props, [
        "type",
        "lazy",
        "displayLoader",
        "loadingClass",
        "isNextImage",
        "noBlur",
        "unoptimized",
        "className",
        "legacyLazyload",
        "testId",
        "loader",
        "priority",
        "blurDataURL",
        "rewriteToOptimizedCdnUrl",
        "src",
        "fetchPriority",
        "decoding",
        "loading",
      ]);
      if (!has(restProps, "width") || !has(restProps, "height")) restProps["layout"] = "fill";
      if (!src) return null;
      // nextjs often uses image in { blurHeight, ... src }
      const imageSrc = (typeof src === "object" ? (src as StaticImageData).src : src) as string;
      const { width, height } = props;

      if (
        !isNextImage ||
        isCloudinaryImage ||
        (rewriteToOptimizedCdnUrl && isUnoptimizedImageUrl(imageSrc))
      ) {
        const optimizedImageSrc = rewriteToOptimizedCdnUrl
          ? getOptimizedImageUrl(imageSrc, width, width ? undefined : height)
          : imageSrc;
        // Extract string from result in case getOptimizedImageUrl returns an object
        const imageSrcString =
          typeof optimizedImageSrc === "object"
            ? (optimizedImageSrc as StaticImageData)?.src
            : optimizedImageSrc;
        return (
          <img
            src={imageSrcString}
            alt={alt || imageSrcString}
            loading={loading || (priority ? "eager" : "lazy")}
            decoding={decoding || (priority ? "sync" : "async")}
            {...(fetchPriority ? { fetchPriority } : {})}
            className={classNames(
              { lazyload: legacyLazyload },
              styles.multimediaImageBlock,
              className,
              { [cloudinaryContainerClass]: isCloudinaryImage },
            )}
            data-testid={testId}
            onError={onError}
            {...restProps}
          />
        );
      }

      return (
        <Image
          src={
            rewriteToOptimizedCdnUrl
              ? getOptimizedImageUrl(src, width, width ? undefined : height)
              : src
          }
          alt={alt || imageSrc}
          loader={loader}
          className={classNames(
            { lazyload: legacyLazyload },
            styles.multimediaNextImageBlock,
            className,
          )}
          data-testid={testId}
          priority={priority}
          placeholder={noBlur ? "empty" : "blur"}
          blurDataURL={BLUR_DATA_URL}
          onError={onError}
          unoptimized={unoptimized || isSvgFormat(getFileExtension(imageSrc))}
          {...restProps}
        />
      );
    }

    case "video": {
      restProps = omit(props, [
        "type",
        "lazy",
        "src",
        "className",
        "testId",
        "loader",
        "priority",
        "unoptimized",
        "blurDataURL",
      ]);
      return (
        <video
          className={classNames({ lazyload: legacyLazyload }, props.className)}
          data-testid={testId}
          {...restProps}
        >
          <source src={src} />
        </video>
      );
    }

    case "iframe": {
      restProps = omit(props, [
        "type",
        "lazy",
        "className",
        "testId",
        "loader",
        "priority",
        "unoptimized",
        "blurDataURL",
      ]);
      return (
        <iframe
          className={classNames({ lazyload: legacyLazyload }, props.className)}
          data-testid={testId}
          {...restProps}
        />
      );
    }

    default:
      return <></>;
  }
};

export default Multimedia;
